import { theme as fdsTheme } from '@fountain/fountain-ui-components';

// Ripple emits its tokens through Tailwind v4's `prefix(ds)`, so the custom
// property is `--ds-color-neutral-*` rather than `--color-neutral-*`. The
// stylesheet is loaded once in `app.js`; the hex fallback covers any context
// that renders without it.
const RIPPLE_NEUTRAL: Record<string, [token: string, hex: string]> = {
  '#131e40': ['neutral-900', '#0f172a'],
  '#141414': ['neutral-900', '#0f172a'],
  '#344063': ['neutral-600', '#404d5f'],
  '#666f89': ['neutral-400', '#687281'],
  '#969eb3': ['neutral-400', '#687281'],
  '#b4b9cc': ['neutral-300', '#b7c0cb'],
  '#d6d9e4': ['neutral-200', '#e2e8f0'],
  '#e9ebf2': ['neutral-200', '#e2e8f0'],
  '#f2f3f7': ['neutral-100', '#f1f5f9'],
  '#fbfbfa': ['neutral-50', '#f8fafc'],
};

const cssVar = (token: string, hex: string) => `var(--ds-color-${token}, ${hex})`;

// SVG presentation attributes, canvas and chart libraries parse their value
// outside the CSS cascade, where `var()` never resolves. Those call sites read
// the raw hex here so the ramp still has one source.
export const rippleNeutral = Object.fromEntries(
  Object.values(RIPPLE_NEUTRAL).map(([token, hex]) => [token, hex]),
) as Record<string, string>;

// FDS writes gray800 as an rgb triple in two theme values, which the hex map
// cannot match. `color-mix` carries the alpha while keeping the color itself
// on the variable; a hex-valued variable cannot sit inside `rgba()`. Keyed on
// the triple so the black shadows and the blue focus outline are left alone.
const FDS_GRAY800_RGB = /rgba?\(\s*19\s*,\s*30\s*,\s*64\s*(?:,\s*([\d.]+)\s*)?\)/gi;

const [GRAY800_TOKEN, GRAY800_HEX] = RIPPLE_NEUTRAL['#131e40'];

const HEX_ONLY = Object.entries(RIPPLE_NEUTRAL).map(
  ([fdsColor, [, hex]]) => [fdsColor, hex] as const,
);

// Material-UI parses these palette values in JavaScript while it builds
// component styles — `fade`, `darken` and `lighten` all reject `var()` and
// throw. Everything under `palette` keeps a literal hex for that reason,
// except `common`, which holds the FDS ramp that Material-UI never reads.
const isMuiParsed = (path: string[]) =>
  path[0] === 'palette' && path.length > 1 && path[1] !== 'common';

const replaceFdsGrays = (value: unknown, path: string[] = []): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => replaceFdsGrays(item, path));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        replaceFdsGrays(nestedValue, [...path, key]),
      ]),
    );
  }

  if (typeof value === 'string') {
    if (isMuiParsed(path)) {
      return HEX_ONLY.reduce(
        (result, [fdsColor, hex]) =>
          result.replace(new RegExp(fdsColor, 'gi'), hex),
        value,
      ).replace(FDS_GRAY800_RGB, (_, alpha?: string) =>
        alpha === undefined
          ? GRAY800_HEX
          : `rgba(15, 23, 42, ${alpha})`,
      );
    }

    const hexMapped = Object.entries(RIPPLE_NEUTRAL).reduce(
      (result, [fdsColor, [token, hex]]) =>
        result.replace(new RegExp(fdsColor, 'gi'), cssVar(token, hex)),
      value,
    );

    return hexMapped.replace(FDS_GRAY800_RGB, (_, alpha?: string) => {
      const color = cssVar(GRAY800_TOKEN, GRAY800_HEX);
      if (alpha === undefined) {
        return color;
      }
      return `color-mix(in srgb, ${color} ${Number(alpha) * 100}%, transparent)`;
    });
  }

  return value;
};

// The clone keeps every key and passes functions through by reference, so the
// shape matches; the traversal cannot prove that to the compiler.
const theme = replaceFdsGrays(fdsTheme) as typeof fdsTheme;

export default theme;
